import { useCallback, useState } from 'react'
import { useToast } from '@chakra-ui/react'
import imageCompression from 'browser-image-compression'
import { v4 as uuidv4 } from 'uuid'

import { SetStateFn } from '~config/constants'
import { useStorage } from '~utils/hooks/use-storage'
import { Attachment, AttachmentType } from '~graphql/generated/graphql'

/**
 * A file ready to be processed into an attachment. `content` is always a
 * data URL (the format produced by both the file picker and a clipboard read).
 */
export interface FileWithContent {
    name: string
    content: string
    type: AttachmentType
}

interface ProcessedAttachmentData {
    name: string
    url: string
    type: AttachmentType
}

const MAX_FILE_SIZE_MB = 1.6
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

/** Maximum number of attachments allowed on a single chat message. */
export const MAX_CHAT_ATTACHMENTS = 4

type UploadFn = (
    path: string,
    newName: string,
    dataURL: string,
) => Promise<string>

async function processFile(
    chatId: string,
    file: FileWithContent,
    uploadFn: UploadFn,
): Promise<ProcessedAttachmentData | null> {
    let { name, content, type } = file

    if (type === AttachmentType.Image && name.toLowerCase().endsWith('.webp')) {
        try {
            const response = await fetch(content)
            const blob = (await response.blob()) as File

            const options = {
                maxSizeMB: MAX_FILE_SIZE_MB,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: 'image/png',
            }
            const convertedBlob = await imageCompression(blob, options)
            const newContent =
                await imageCompression.getDataUrlFromFile(convertedBlob)
            const newName = name.replace(/\.webp$/i, '.png')

            content = newContent
            name = newName
        } catch (error) {
            console.error(
                'Failed to convert WEBP to PNG, falling back to original file:',
                name,
                error,
            )
        }
    }

    if (type === AttachmentType.Video) {
        try {
            const uniqueName = `${uuidv4()}-${name}`
            const storagePath = `chats/${chatId}/attachments`
            const uploadedUrl = await uploadFn(storagePath, uniqueName, content)

            if (!uploadedUrl) {
                console.error('Upload failed for video:', name)
                return null
            }
            return { name, url: uploadedUrl, type }
        } catch (error) {
            console.error('Error uploading video:', name, error)
            return null
        }
    } else if (
        type === AttachmentType.Image &&
        content.length > MAX_FILE_SIZE_BYTES
    ) {
        try {
            const response = await fetch(content)
            const blob = (await response.blob()) as File

            const options = {
                maxSizeMB: MAX_FILE_SIZE_MB,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
            }
            const compressedBlob = await imageCompression(blob, options)
            const base64 =
                await imageCompression.getDataUrlFromFile(compressedBlob)
            return { name, url: base64, type }
        } catch (error) {
            console.error('Error compressing image:', name, error)
            return { name, url: content, type }
        }
    } else {
        return { name, url: content, type }
    }
}

function readBlobAsDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = () =>
            reject(reader.error ?? new Error('Failed to read clipboard image'))
        reader.readAsDataURL(blob)
    })
}

/**
 * Extracts pasted images from clipboard data and converts them into
 * {@link FileWithContent} entries ready to be passed to `addFiles`.
 * Returns an empty array when the clipboard contains no images.
 */
export async function getImageFilesFromClipboard(
    items: DataTransferItemList,
): Promise<FileWithContent[]> {
    const imageItems = Array.from(items).filter(
        item => item.kind === 'file' && item.type.startsWith('image/'),
    )

    const files = await Promise.all(
        imageItems.map(async (item): Promise<FileWithContent | null> => {
            const blob = item.getAsFile()
            if (!blob) return null

            const content = await readBlobAsDataURL(blob)
            const extension = item.type.split('/')[1]?.split('+')[0] || 'png'

            return {
                name: `pasted-image-${uuidv4()}.${extension}`,
                content,
                type: AttachmentType.Image,
            }
        }),
    )

    return files.filter((file): file is FileWithContent => file !== null)
}

interface UseAddAttachmentsArgs {
    chatId: string
    setAttachments: SetStateFn<Attachment[]>
    maxAttachments: number
}

interface UseAddAttachments {
    /**
     * Processes (compresses/uploads as needed) and appends the given files to
     * the current attachments, deduping by url (same-named files are allowed)
     * and capping at `maxAttachments`. Surfaces a toast if any file fails to
     * process.
     */
    addFiles: (files: FileWithContent[]) => Promise<void>
    isProcessing: boolean
    isUploading: boolean
}

/**
 * Shared logic for turning raw files (from the file picker or a clipboard
 * paste) into chat attachments.
 */
export function useAddAttachments({
    chatId,
    setAttachments,
    maxAttachments,
}: UseAddAttachmentsArgs): UseAddAttachments {
    const toast = useToast()
    const { upload, uploading: isUploading } = useStorage()
    const [isProcessing, setIsProcessing] = useState(false)

    const addFiles = useCallback(
        async (files: FileWithContent[]) => {
            if (!files.length) return

            setIsProcessing(true)
            try {
                // Process the snapshot as-is. Callers should clear the file
                // picker before awaiting this so effect deps cannot re-enter
                // with the same selection (compression changes the data URL,
                // so content-based dedupe against staged attachments is unsafe).
                const results = await Promise.all(
                    files.map(file => processFile(chatId, file, upload)),
                )
                const successfulUploads = results.filter(
                    (result): result is ProcessedAttachmentData =>
                        result !== null,
                )

                if (results.length !== successfulUploads.length) {
                    const failedCount =
                        results.length - successfulUploads.length
                    toast({
                        title: 'Upload Error',
                        description: `${failedCount} file(s) failed to upload or process. Please try again.`,
                        status: 'error',
                        position: 'top',
                        isClosable: true,
                    })
                }

                if (successfulUploads.length > 0) {
                    setAttachments(prev => {
                        const newAttachments = successfulUploads.filter(
                            procFile =>
                                !prev.some(
                                    existing => existing.url === procFile.url,
                                ),
                        )

                        return [
                            ...prev,
                            ...newAttachments.map(procFile => ({
                                id: uuidv4(),
                                filename: procFile.name,
                                url: procFile.url,
                                type: procFile.type,
                            })),
                        ].slice(-maxAttachments)
                    })
                }
            } finally {
                setIsProcessing(false)
            }
        },
        [chatId, setAttachments, maxAttachments, upload, toast],
    )

    return { addFiles, isProcessing, isUploading }
}
